import React, { useState, useMemo } from "react";
import { View, Pressable, StyleSheet, ActivityIndicator, ScrollView } from "react-native";
import { FileText } from "lucide-react";
import CustomModal from "../../../common/general/CustomModal/CustomModal";
import CustomText from "../../../common/general/CustomText/CustomText";
import CustomTextInput from "../../../common/general/CustomTextInput/CustomTextInput";
import { useGetAssignmentsWithCourseQuery, useGetAllCompanionChatsForUserQuery } from "@axio/graphql/generated/graphql";
import { useAppConfig } from "../../../../AppConfigProvider";
import { useWorkspaceThreads } from "../../../../contexts/CompanionThreadsContext";
import { colors, spacing, radii, typographyScale } from "../../../../styles/design-system";

export interface Assignment {
  id: string;
  title?: string | null;
  text?: string | null;
  due_date?: string | null;
  points_possible?: number | null;
  courseName?: string;
  courseCode?: string;
  courseId?: string;
}

interface AddAssignmentModalProps {
  visible: boolean;
  onClose: () => void;
  onSelectAssignment: (assignment: Assignment) => void;
}

const GENERAL_WORKSPACE_ID = "general_chats";

export const AddAssignmentModal: React.FC<AddAssignmentModalProps> = ({ visible, onClose, onSelectAssignment }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const appConfig = useAppConfig();
  const schoolId = appConfig?.axioSchools[0];
  const { activeWorkspaceId } = useWorkspaceThreads();

  const { data: chatsData } = useGetAllCompanionChatsForUserQuery({});
  const courseWorkspaces = chatsData?.getAllCompanionChatsForUser?.courseWorkspaces ?? [];

  const isGeneralWorkspace = !activeWorkspaceId || activeWorkspaceId === GENERAL_WORKSPACE_ID;

  // Get the course ID if we're in a course workspace ${userId}|${courseId}
  const courseId = useMemo(() => {
    if (isGeneralWorkspace) return null;
    const workspace = courseWorkspaces.find((ws) => ws.id === activeWorkspaceId);
    if (!workspace) {
      return null;
    }

    const key = workspace.key;

    if (!key || typeof key !== "string") {
      console.warn("Workspace key not available.");
      return null;
    }

    const parts = key.split("|");
    const extractedCourseId = parts.length === 2 ? parts[1] : null;
    return extractedCourseId;
  }, [activeWorkspaceId, courseWorkspaces, isGeneralWorkspace]);

  const { data, loading } = useGetAssignmentsWithCourseQuery({
    variables: {
      schoolId,
      searchQuery: searchQuery.trim(),
      courseId: courseId || undefined,
    },
    skip: !schoolId,
    fetchPolicy: "network-only",
  });

  const groupedAssignments = useMemo(() => {
    if (!data?.getAssignmentsWithCourse?.data) return [];

    const grouped: {
      [key: string]: { courseName: string; courseCode?: string | null; assignments: Assignment[] };
    } = {};

    data.getAssignmentsWithCourse.data.forEach((assignment) => {
      const courseName = assignment.courseName || "Other";
      const courseCode = assignment.courseCode;
      if (!grouped[courseName]) {
        grouped[courseName] = { courseName, courseCode, assignments: [] };
      }
      grouped[courseName].assignments.push(assignment as Assignment);
    });

    return Object.values(grouped);
  }, [data]);

  const handleSelect = (assignment: Assignment) => {
    onSelectAssignment(assignment);
    setSearchQuery("");
    onClose();
  };

  const renderAssignmentItem = ({ item }: { item: Assignment }) => {
    const isHovered = hoveredId === item.id;
    return (
      <Pressable
        style={[styles.assignmentItem, isHovered && styles.assignmentItemHovered]}
        onPress={() => handleSelect(item)}
        onHoverIn={() => setHoveredId(item.id)}
        onHoverOut={() => setHoveredId(null)}>
        <View style={styles.assignmentContent}>
          <View style={styles.assignmentTitleRow}>
            <FileText size={20} color={colors.brand[500]} style={styles.assignmentIcon} />
            <CustomText
              text={item.title || "Untitled Assignment"}
              useTranslationText={false}
              style={styles.assignmentName}
              numberOfLines={1}
            />
          </View>
          {item.due_date && (
            <CustomText
              text={`Due ${new Date(item.due_date).toLocaleDateString(undefined, {
                weekday: "short",
                month: "short",
                day: "numeric",
              })}`}
              useTranslationText={false}
              style={styles.assignmentDate}
            />
          )}
        </View>
      </Pressable>
    );
  };

  const renderCourseSection = ({
    item,
  }: {
    item: { courseName: string; courseCode?: string | null; assignments: Assignment[] };
  }) => (
    <View style={styles.courseSection}>
      <CustomText
        text={item.courseCode ? `${item.courseName} (${item.courseCode})` : item.courseName}
        useTranslationText={false}
        style={styles.courseName}
      />
      {item.assignments.map((assignment) => (
        <View key={assignment.id}>{renderAssignmentItem({ item: assignment })}</View>
      ))}
    </View>
  );

  const hasSearch = searchQuery.trim().length > 0;
  const hasData = groupedAssignments.length > 0;

  const courseName = useMemo(() => {
    if (isGeneralWorkspace) return null;
    // Find the first assignment with a courseName, or check the workspace data
    const firstAssignment = data?.getAssignmentsWithCourse?.data?.find((a) => a.courseName);
    if (firstAssignment?.courseName) {
      return firstAssignment.courseName;
    }
    // Fallback: get course name from workspace data if we have a courseId but no assignments
    if (courseId) {
      const workspace = courseWorkspaces.find((ws) => ws.id === activeWorkspaceId);
      return workspace?.name || null;
    }
    return null;
  }, [isGeneralWorkspace, data, courseId, courseWorkspaces, activeWorkspaceId]);

  const subtitleText =
    isGeneralWorkspace || !courseName
      ? "Select an assignment from your courses to attach to your message"
      : `Select an assignment from ${courseName} to attach to your message`;

  const emptyStateText =
    isGeneralWorkspace || !courseName
      ? hasSearch
        ? "No assignments found"
        : "No assignments available"
      : hasSearch
        ? "No assignments found"
        : `No assignments available for this course`;

  return (
    <CustomModal
      visible={visible}
      onRequestClose={onClose}
      title="Add Assignment"
      showCloseButton
      ariaLabel="Add Assignment Modal">
      <View style={styles.modalContent}>
        <CustomText text={subtitleText} useTranslationText={false} style={styles.subtitle} />

        <CustomTextInput
          placeholder="Search assignments..."
          value={searchQuery}
          onChangeText={setSearchQuery}
          style={styles.searchInput}
        />

        {loading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={colors.brand[500]} />
          </View>
        ) : !hasData ? (
          <View style={styles.emptyContainer}>
            <CustomText text={emptyStateText} useTranslationText={false} style={styles.emptyText} />
          </View>
        ) : (
          <ScrollView style={styles.assignmentList} contentContainerStyle={styles.assignmentListContent} tabIndex={0}>
            {groupedAssignments.map((item) => (
              <View key={item.courseName}>{renderCourseSection({ item })}</View>
            ))}
          </ScrollView>
        )}
      </View>
    </CustomModal>
  );
};

const styles = StyleSheet.create({
  modalContent: {
    width: "100%",
    maxHeight: 600,
  },
  subtitle: {
    fontSize: typographyScale.textSm.fontSize,
    color: colors.grey[500],
    marginBottom: spacing.md,
  },
  searchInput: {
    marginBottom: spacing.sm,
    // @ts-expect-error - outlineOffset is a valid web CSS property for React Native Web
    outlineOffset: 0,
  },
  assignmentList: {
    maxHeight: 400,
  },
  assignmentListContent: {
    paddingTop: spacing.sm,
    paddingBottom: spacing.xxs,
  },
  courseSection: {
    marginBottom: spacing.md,
  },
  courseName: {
    fontSize: typographyScale.textXs.fontSize,
    fontWeight: "600",
    color: colors.grey[500],
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginBottom: spacing.xs,
    paddingHorizontal: spacing.xxs,
  },
  assignmentItem: {
    paddingVertical: 12,
    paddingHorizontal: spacing.sm,
    borderRadius: radii.lg,
    marginBottom: spacing.xs,
    cursor: "pointer",
    borderWidth: 1,
    borderColor: colors.grey[200],
  },
  assignmentItemHovered: {
    backgroundColor: colors.brand[50],
    borderColor: colors.brand[300],
  },
  assignmentContent: {
    flex: 1,
  },
  assignmentTitleRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: spacing.xxs,
  },
  assignmentIcon: {
    marginRight: spacing.xs,
  },
  assignmentName: {
    fontSize: typographyScale.textSm.fontSize,
    color: colors.grey[900],
    fontWeight: "500",
    flex: 1,
  },
  assignmentDate: {
    fontSize: typographyScale.textXs.fontSize,
    color: colors.grey[500],
    marginLeft: 28,
  },
  loadingContainer: {
    paddingVertical: 60,
    alignItems: "center",
    justifyContent: "center",
  },
  emptyContainer: {
    paddingVertical: 60,
    alignItems: "center",
    justifyContent: "center",
  },
  emptyText: {
    fontSize: typographyScale.textSm.fontSize,
    color: colors.grey[500],
  },
});
